import * as React from "react";
import { Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "../../components/mui";
import { DashboardRoundedIcon, StarRoundedIcon } from "../../components/icons";

import "../../styles/main.css";
import "../../styles/pages/dashboard.css";
import PageLayout from "../../components/shared/PageLayout";
import PageHeader from "../../components/shared/PageHeader";
import DataTableCard from "../../components/shared/DataTableCard";

// Hardcoded data - Backend developer will replace with API calls
const STATUS_CONFIG = {
  "within-30": {
    label: "Within 30 - day notice period",
    className: "dashboard-status--within-30"
  },
  "beyond-30": {
    label: "Beyond 30 - day notice period",
    className: "dashboard-status--beyond-30"
  },
  "within-90": {
    label: "Within 90 - day notice period",
    className: "dashboard-status--within-90"
  },
  expired: {
    label: "Expired Contracts",
    className: "dashboard-status--expired"
  }
};

const RENTAL_DUE_LEGEND = ["within-30", "beyond-30"];
const EXPIRING_CONTRACTS_LEGEND = ["within-90", "expired"];

// Hardcoded rental due data - Backend developer will fetch from API
const RENTAL_DUE_ROWS = [
  {
    status: "within-30",
    unit: "28F Three Central",
    condo: "Three Central",
    dueDate: "01/12/2024",
    period: "Dec 01 - Jan 31 2025",
    amount: "80,000.00",
    lessee: "LOG"
  },
  {
    status: "within-30",
    unit: "15C 141 Apartment",
    condo: "141 Apartment",
    dueDate: "07/02/2025",
    period: "Feb 07 - Aug 06 2025",
    amount: "650.00",
    lessee: "JK"
  },
  {
    status: "beyond-30",
    unit: "28F Three Central",
    condo: "Three Central",
    dueDate: "01/02/2025",
    period: "Feb 01 - Mar 31 2025",
    amount: "80,000.00",
    lessee: "LOG"
  },
  {
    status: "beyond-30",
    unit: "28F Three Central",
    condo: "Three Central",
    dueDate: "01/02/2025",
    period: "Apr 01 - May 31 2025",
    amount: "80,000.00",
    lessee: "LOG"
  },
  {
    status: "beyond-30",
    unit: "28F Three Central",
    condo: "Three Central",
    dueDate: "01/06/2025",
    period: "Jun 01 - Jul 31 2025",
    amount: "80,000.00",
    lessee: "LOG"
  },
  {
    status: "beyond-30",
    unit: "68 Philip Street",
    condo: "Muntinlupa Village, Paranaque CT",
    dueDate: "11/06/2025",
    period: "Jun 01 - Jun 30 2025",
    amount: "80,000.00",
    lessee: "Ousan Solution Inc."
  },
  {
    status: "beyond-30",
    unit: "15C 141 Apartment",
    condo: "141 Apartment",
    dueDate: "07/02/2025",
    period: "Aug 07 - Sep 06 2025",
    amount: "650.00",
    lessee: "LOG"
  },
  {
    status: "expired",
    unit: "28F Three Central",
    condo: "Three Central",
    dueDate: "01/08/2025",
    period: "Aug 01 - Sep 30 2025",
    amount: "80,000.00",
    lessee: "LOG"
  },
  {
    status: "expired",
    unit: "28F Three Central",
    condo: "Three Central",
    dueDate: "01/10/2025",
    period: "Oct 01 - Nov 30 2025",
    amount: "80,000.00",
    lessee: "LOG"
  },
  {
    status: "expired",
    unit: "28F Three Central",
    condo: "Three Central",
    dueDate: "01/12/2025",
    period: "Dec 01 - Jan 31 2026",
    amount: "80,000.00",
    lessee: "LOG"
  }
];

// Hardcoded expiring contracts data - Backend developer will fetch from API
const EXPIRING_CONTRACT_ROWS = [
  {
    status: "within-90",
    unit: "15C 141 Apartment",
    condo: "141 Apartment",
    contractEndDate: "06/02/2026",
    monthlyRent: "(PHP) 106.33",
    lessee: "JK"
  }
];

function NoticeChip({ status }) {
  const config = STATUS_CONFIG[status];

  if (!config) {
    return null;
  }

  return (
    <Chip
      size="small"
      icon={<StarRoundedIcon />}
      label={config.label}
      className={`dashboard-status-chip ${config.className}`}
    />
  );
}

/**
 * Dashboard Page Component
 * UI/Layout only - Backend developer will add:
 * - API calls for fetching rental due data
 * - API calls for fetching expiring contracts
 * - Real-time data updates
 * - Pagination logic
 * - Filtering and sorting
 * - Export functionality
 */
export default function Dashboard({ onLogout, onNavigate }) {
  return (
    <PageLayout activeKey="dashboard" onLogout={onLogout} onNavigate={onNavigate} className="page-content">
          <PageHeader
            icon={DashboardRoundedIcon}
            title="Dashboard"
            summary="Track upcoming dues and expiring leases to keep follow-ups on schedule."
          />

          <section className="dashboard-table-grid" aria-label="Dashboard tables">
            <div className="dashboard-table-container">
              <section className="dashboard-table-legend" aria-label="Rental due legend">
                {RENTAL_DUE_LEGEND.map((key) => (
                  <Chip
                    key={key}
                    icon={<StarRoundedIcon />}
                    label={STATUS_CONFIG[key].label}
                    className={`dashboard-legend-chip ${STATUS_CONFIG[key].className}`}
                  />
                ))}
              </section>
              <DataTableCard
                title="Condo Unit Rental Due for Payment"
                totalEntries={11}
                startEntry={1}
                endEntry={10}
                currentPage={1}
                totalPages={2}
              >
              <TableContainer className="data-table-wrapper">
                <Table size="small" aria-label="Rental due table">
                  <TableHead>
                    <TableRow>
                      <TableCell className="data-table-status-column" />
                      <TableCell>Condo Unit</TableCell>
                      <TableCell>Condo</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Lessee</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {RENTAL_DUE_ROWS.map((row, index) => (
                      <TableRow hover key={`${row.unit}-${index}`}>
                        <TableCell className="dashboard-status-column">
                          <StarRoundedIcon className={`dashboard-status-icon ${STATUS_CONFIG[row.status].className}`} />
                        </TableCell>
                        <TableCell>{row.unit}</TableCell>
                        <TableCell>{row.condo}</TableCell>
                        <TableCell>{row.dueDate}</TableCell>
                        <TableCell>{row.period}</TableCell>
                        <TableCell align="right">{row.amount}</TableCell>
                        <TableCell>{row.lessee}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </DataTableCard>
            </div>

            <div className="dashboard-table-container">
              <section className="dashboard-table-legend" aria-label="Expiring contracts legend">
                {EXPIRING_CONTRACTS_LEGEND.map((key) => (
                  <Chip
                    key={key}
                    icon={<StarRoundedIcon />}
                    label={STATUS_CONFIG[key].label}
                    className={`dashboard-legend-chip ${STATUS_CONFIG[key].className}`}
                  />
                ))}
              </section>
              <DataTableCard
                title="Expiring Condo Unit Contracts"
                totalEntries={1}
                startEntry={1}
                endEntry={1}
                currentPage={1}
                totalPages={1}
              >
              <TableContainer className="data-table-wrapper">
                <Table size="small" aria-label="Expiring contracts table">
                  <TableHead>
                    <TableRow>
                      <TableCell className="data-table-status-column" />
                      <TableCell>Condo Unit</TableCell>
                      <TableCell>Condo</TableCell>
                      <TableCell>Contract End Date</TableCell>
                      <TableCell className="align-right">Monthly Rent</TableCell>
                      <TableCell>Lessee</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {EXPIRING_CONTRACT_ROWS.map((row, index) => (
                      <TableRow hover key={`${row.unit}-${index}`}>
                        <TableCell className="dashboard-status-column">
                          <StarRoundedIcon className={`dashboard-status-icon ${STATUS_CONFIG[row.status].className}`} />
                        </TableCell>
                        <TableCell>{row.unit}</TableCell>
                        <TableCell>{row.condo}</TableCell>
                        <TableCell>{row.contractEndDate}</TableCell>
                        <TableCell className="align-right">{row.monthlyRent}</TableCell>
                        <TableCell>{row.lessee}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </DataTableCard>
            </div>
          </section>
    </PageLayout>
  );
}
